import React from 'react'
import { AppForms } from '@/app'
import { AppStatus } from '@/redux'
import { APIClient } from '@/services'
import { AnyFunction, useForm } from '@codeleap/common'
import { Replace, TypeGuards } from '@codeleap/types'
import { Organisation } from '@/types'
import { createLog } from '@/utils'

type SanitizedProfile = Replace<APIClient.Session.Profile, 'organization', Organisation['id']>

const DEBUG = false
const log = createLog('useAnswerProfile', DEBUG)

export function useAnswerProfile(options: { onSuccess?: AnyFunction } = {}) {
  const { onSuccess } = options

  const { updateProfile } = APIClient.Session.useSession()
  const { data: profile } = APIClient.Session.useProfile()

  const form = useForm(AppForms.editProfile, {
    initialState: { ...profile },
  })

  log('Hook initialized', { profile, formValues: form.values })

  const onUpdate = React.useCallback(
    async (key: keyof SanitizedProfile) => {
      const value = form.values[key]
      log('onUpdate called', { key, value })
      if (!value) {
        log('onUpdate aborted - no value', { key })
        return
      }

      const payload: SanitizedProfile = { ...(profile as SanitizedProfile), [key]: value }
      if (key === 'organization' && TypeGuards.isObject(value)) {
        log('Organization detected, using ID', { value, id: (value as Organisation).id })
        payload.organization = (value as Organisation).id
      }

      try {
        AppStatus.set('loading')
        log('Updating profile', { key, payload })

        await updateProfile.mutateAsync(payload as SanitizedProfile)
        APIClient.Session.QueryKeys.me.refresh()

        log('Profile updated successfully', { key })
        onSuccess?.()

        AppStatus.set('done')
      } catch (error) {
        log('Error updating profile', { key, error })
        logger.error('Error updating profile', error, `Profile Prompt - ${key}`)
      } finally {
        AppStatus.set('idle')
      }
    },
    [form, profile],
  )

  const isAnswered = React.useCallback(
    (key: keyof SanitizedProfile) => {
      const answer = profile[key]
      const answered = Array.isArray(answer) ? answer.length > 0 : !TypeGuards.isNil(answer)
      log('isAnswered check', { key, answer, answered })
      return answered
    },
    [profile],
  )

  return { onUpdate, isAnswered, form }
}

export type UseAnswerProfile = ReturnType<typeof useAnswerProfile>
